// PRD-0190 publish handoff: the operator marks an approved blog post Ready to publish; the brand's
// site agent claims it, publishes it in the site repo and reports the live URL back.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { isNotFoundError } from "../lib/load-state";
import type { PublishHandoff } from "../../../shared/publish-handoff";

const ACTIVE = new Set(["ready", "claimed", "verifying"]);

function handoffPath(brandId: string, briefId: string) {
  return `/api/v1/brands/${encodeURIComponent(brandId)}/publish-handoffs/${encodeURIComponent(briefId)}`;
}

export function publishHandoffQueryKey(brandId: string | undefined, briefId: string | undefined) {
  return ["publish-handoff", brandId, briefId] as const;
}

/** null = no handoff yet (404). Polls while a site agent may be working on it. */
export function usePublishHandoff(brandId: string | undefined, briefId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: publishHandoffQueryKey(brandId, briefId),
    queryFn: async (): Promise<PublishHandoff | null> => {
      try {
        return (await api.get<{ data: PublishHandoff }>(handoffPath(brandId!, briefId!))).data;
      } catch (error) {
        if (isNotFoundError(error)) return null;
        throw error;
      }
    },
    enabled: !!brandId && !!briefId && enabled,
    retry: false,
    refetchInterval: (query) => (query.state.data && ACTIVE.has(query.state.data.status) ? 30_000 : false),
  });
}

export function useMarkReadyToPublish(brandId: string | undefined, briefId: string | undefined, campaignId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!brandId || !briefId) throw new Error("Missing brand or brief context");
      return (await api.post<{ data: PublishHandoff }>(`${handoffPath(brandId, briefId)}/ready`, {})).data;
    },
    onSuccess: (handoff) => {
      qc.setQueryData(publishHandoffQueryKey(brandId, briefId), handoff);
    },
    onSettled: () => {
      // A 409 DRAFT_CHANGED_SINCE_APPROVAL revokes approval server-side; refresh the brief too.
      qc.invalidateQueries({ queryKey: publishHandoffQueryKey(brandId, briefId) });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
      if (campaignId) qc.invalidateQueries({ queryKey: ["campaigns", brandId, campaignId] });
    },
  });
}
